/**
 * ProjectSidebar — narrow right rail with two tabs:
 *   Details — name, dates, client, status, priority, budget summary.
 *   People  — managers + members with a per-member progress dropdown, hide
 *             toggle, a progress filter, and a Manage button.
 */
import React, { useState } from "react";
import { toast } from "../../../utils/toast";
import { Settings2, EyeOff } from "lucide-react";
import { wsApi } from "../wsApi";
import { T, Tabs } from "../../../components/em/ui";
import { PROJECT_STATUS, PRIORITY, MEMBER_PROGRESS, fmtDate } from "../wsConstants";
import { money, Avatar } from "../wsUi";
import ManageMembersModal from "./ManageMembersModal";

const PROGRESS_KEYS = ["pending", "in_progress", "review", "completed"];

export default function ProjectSidebar({ project, users, canManage, onChanged }) {
  const [tab, setTab] = useState("details");
  const [manage, setManage] = useState(false);
  const [progressFilter, setProgressFilter] = useState("all");

  const setProgress = async (userId, progress) => {
    try { await wsApi.updateProjectMember(project.id, userId, { progress }); onChanged(); }
    catch { toast.error("Could not update"); }
  };
  const toggleHide = async (m) => {
    try { await wsApi.updateProjectMember(project.id, m.user_id, { hidden: m.hidden ? 0 : 1 }); onChanged(); }
    catch { toast.error("Could not update"); }
  };

  const members = project.members || [];
  const visible = progressFilter === "all" ? members : members.filter((m) => m.progress === progressFilter);

  return (
    <aside style={{ width: 300, flexShrink: 0 }}>
      <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden", position: "sticky", top: 16 }}>
        {/* Tabs — shared segmented control (same as Attendance/Leaves) */}
        <div style={{ padding: "12px 12px 0" }}>
          <Tabs
            tabs={[
              { key: "details", label: "Details" },
              { key: "people", label: `People (${members.length})` },
            ]}
            active={tab} onChange={setTab}
            style={{ display: "flex", width: "100%", boxSizing: "border-box" }}
          />
        </div>

        <div style={{ padding: 16 }}>
          {tab === "details" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <Row label="Name" value={project.name} />
              <Row label="Client" value={project.client_name || "—"} />
              <Row label="Status" value={(PROJECT_STATUS[project.status] || PROJECT_STATUS.planning).label} />
              <Row label="Priority" value={(PRIORITY[project.priority] || PRIORITY.medium).label} />
              <Row label="Start date" value={project.start_date ? fmtDate(project.start_date) : "—"} />
              <Row label="End date" value={project.deadline ? fmtDate(project.deadline) : "—"} />
              <Row label="Budget type" value={project.budget_type ? project.budget_type[0].toUpperCase() + project.budget_type.slice(1) : "—"} />
              <Row label="Budget" value={project.budget != null ? money(project.budget) : "—"} />
              <Row label="Hourly quote" value={project.hourly_quote != null ? money(project.hourly_quote) : "—"} />
            </div>
          ) : (
            <div>
              {/* Progress filter */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {["all", ...PROGRESS_KEYS].map((k) => (
                  <button key={k} onClick={() => setProgressFilter(k)}
                    style={{ padding: "4px 9px", borderRadius: 9999, border: `1px solid ${progressFilter === k ? T.orange : T.border}`,
                      background: progressFilter === k ? T.orange50 : "transparent",
                      color: progressFilter === k ? T.orange : T.fg5, cursor: "pointer", font: `600 10.5px ${T.font}` }}>
                    {k === "all" ? "All" : MEMBER_PROGRESS[k].label}
                  </button>
                ))}
              </div>

              {canManage && (
                <button onClick={() => setManage(true)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 7, width: "100%",
                    padding: "8px 0", marginBottom: 12, borderRadius: 8, border: `1px solid ${T.border}`,
                    background: "var(--em-bg-card)", color: T.fg2, cursor: "pointer", font: `600 12px ${T.font}` }}>
                  <Settings2 size={14} /> Manage people
                </button>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visible.length === 0 && <div style={{ color: T.fg7, font: `400 12px ${T.font}`, textAlign: "center", padding: 10 }}>No one here.</div>}
                {visible.map((m) => (
                  <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 8, opacity: m.hidden ? 0.5 : 1 }}>
                    <Avatar member={m} size={26} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: `600 12.5px ${T.font}`, color: T.fg1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {m.full_name || m.username}
                        {m.role === "manager" && <span style={{ marginLeft: 6, font: `700 9px ${T.font}`, color: T.orange, textTransform: "uppercase" }}>PM</span>}
                        {!!m.hidden && <EyeOff size={11} style={{ marginLeft: 5, verticalAlign: "middle", color: T.fg7 }} />}
                      </div>
                    </div>
                    {canManage ? (
                      <select value={m.progress || "pending"} onChange={(e) => setProgress(m.user_id, e.target.value)}
                        style={{ width: 100, flexShrink: 0, border: `1px solid ${T.border}`, borderRadius: 6, padding: "3px 5px", font: `500 10.5px ${T.font}`,
                          background: (MEMBER_PROGRESS[m.progress] || MEMBER_PROGRESS.pending).bg,
                          color: (MEMBER_PROGRESS[m.progress] || MEMBER_PROGRESS.pending).text }}>
                        {PROGRESS_KEYS.map((k) => <option key={k} value={k}>{MEMBER_PROGRESS[k].label}</option>)}
                      </select>
                    ) : (
                      <span style={{ padding: "3px 8px", borderRadius: 9999, font: `600 10px ${T.font}`,
                        background: (MEMBER_PROGRESS[m.progress] || MEMBER_PROGRESS.pending).bg,
                        color: (MEMBER_PROGRESS[m.progress] || MEMBER_PROGRESS.pending).text }}>
                        {(MEMBER_PROGRESS[m.progress] || MEMBER_PROGRESS.pending).label}
                      </span>
                    )}
                    {canManage && (
                      <button title={m.hidden ? "Show" : "Hide"} onClick={() => toggleHide(m)}
                        style={{ background: "none", border: 0, cursor: "pointer", color: T.fg7, display: "flex", padding: 2 }}>
                        <EyeOff size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {manage && (
        <ManageMembersModal project={project} users={users}
          onClose={() => setManage(false)} onChanged={onChanged} />
      )}
    </aside>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div style={{ font: `600 10.5px ${T.font}`, color: T.fg7, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ font: `500 13px ${T.font}`, color: T.fg1, marginTop: 2, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}
